<template>
  <div class="weighing_on_scale_list" v-if="canView" v-cloak>
    <GridPanelHeader
      :grid-ref="$refs[gridRefKey]"
      :title="$t('scale.weighing_on_scale.title')"
      :show-add="canEdit"
      :show-filter="true"
      @filter="openFilterPanel"
      @export="exportGrid"
      @add="handleAddClick"
      :show-refresh="true"
    />

    <GridFilter
      :visible="filterPanelVisible"
      :title="$t('common.filter')"
      @apply="applyFilters"
      @reset="resetFilters"
      @close="closeFilterPanel"
    >
      <div class="filter-field no-border">
        <label>{{ $t("common.created_by") }}</label>
        <DxTagBox
          :data-source="users"
          value-expr="uuid"
          display-expr="name"
          v-model="filters.created_by_user_uuids"
          :showClearButton="true"
          :searchEnabled="true"
          :applyValueMode="'useButtons'"
          :showSelectionControls="true"
          :selectAllMode="'allPages'"
          :selectAllText="$t('select_all')"
          :noDataText="$t('common.no_data_found')"
          :placeholder="$t('user.select_user')"
          class="scale-filter-users-tagbox"
        />
      </div>
      <DateRangeFilter :dateFrom.sync="filters.date_from" :dateTo.sync="filters.date_to" />
    </GridFilter>

    <DxDataGrid
      :ref="gridRefKey"
      :data-source="weighingOnScaleDataSurce"
      :remote-operations="gridRemoteOperations"
      :column-min-width="80"
      style="min-height: 350px"
      :show-column-lines="true"
      :show-row-lines="false"
      :show-borders="true"
      :column-auto-width="true"
      :allowColumnReordering="true"
      :allowColumnResizing="true"
      :row-alternation-enabled="true"
      :searchPanel="{ visible: true, width: 240, placeholder: 'Search...' }"
      :element-attr="gridAttributes"
      @exporting="onExporting"
      @editing-start="onEditingStart"
      @option-changed="onOptionChanged"
      @content-ready="onContentReady"
      @row-prepared="onRowPrepared"
      :row-height="50"
    >
      <DxColumn
        width="50"
        :fixed="true"
        :css-class="'scale-download-pdf-cell'"
        fixedPosition="left"
        :showInColumnChooser="false"
        cell-template="cellTemplatePdf"
      >
      </DxColumn>
      <template #cellTemplatePdf="{ data }">
        <div>
          <img
            v-if="data?.data?.finished_weighing == 1 /* && !data.data.deleted_at*/"
            src="~@/assets/images/desktop/icons/controls/pdf.svg"
            class="icon-svg danger-filter"
            @click="handleDownloadPdf(data)"
            :title="
              $t('common.download_document_pdf', {
                document: 'BON DE CÂNTAR',
              })
            "
          />
          <div v-else style="display: inline-block; width: 29px; height: 24px"></div>
          <img
            v-if="!!data.data.field_delivery_note_document && !!data.data.field_delivery_note_download_url"
            src="~@/assets/images/desktop/icons/controls/pdf.svg"
            class="icon-svg danger-filter"
            @click="handleDownloadPdf(data, true)"
            :title="
              $t('common.download_document_pdf', {
                document: 'AVIZ DE ÎNSOȚIRE A MĂRFII',
              })
            "
          />
        </div>
      </template>
      <DxColumn
        dataField="weighing_type_id"
        cell-template="weighingTypeViewTemplate"
        :caption="$t('common.weighing_type')"
        :allowHeaderFiltering="false"
        :filter-operations="['contains']"
        :width="150"
        :calculateCellValue="getWeighingTypeName"
      >
        <DxLookup :data-source="localWeighingTypes" value-expr="weighing_type_id" display-expr="weighing_type" />
      </DxColumn>
      <template #weighingTypeViewTemplate="{ data }">
        <span>
          {{ data.data.weighing_type }}
        </span>
      </template>
      <DxColumn
        dataField="unique_id"
        :caption="$t('scale.weighing_on_scale.scaling_identifier')"
        :allowHeaderFiltering="false"
        :filter-operations="['contains']"
        :allowSorting="false"
        :allowFiltering="true"
        :allowEditing="false"
        :visible="fieldReportUsaAutomationEnabled"
        :calculateCellValue="getUniqueIdColumnText"
        :width="120"
      />
      <DxColumn
        dataField="finished_weighing"
        cell-template="finishedWeighingViewTemplate"
        :caption="$t('scale.weighing_on_scale.finished_weighing')"
        :allowHeaderFiltering="false"
        :filter-operations="['=']"
        :allowSorting="false"
        :allowFiltering="true"
        :allowEditing="false"
        :min-width="100"
        :calculateCellValue="getYesNoColumnText"
      >
        <DxLookup :data-source="yes_no_list" value-expr="id" display-expr="label" />
      </DxColumn>
      <template #finishedWeighingViewTemplate="{ data }">
        <span>
          {{ getYesNoColumnText(data?.data?.finished_weighing) }}
        </span>
      </template>
      <DxColumn
        dataField="has_field_delivery_note"
        :caption="$t('common.field_report')"
        cell-template="fieldDeliveryNoteViewTemplate"
        :allowHeaderFiltering="false"
        :filter-operations="['=']"
        :allowSorting="false"
        :allowFiltering="true"
        :allowEditing="false"
        :min-width="100"
        :calculateCellValue="getYesNoColumnText"
      >
         <DxLookup :data-source="yes_no_list" value-expr="id" display-expr="label" />
      </DxColumn>
      <template #fieldDeliveryNoteViewTemplate="{ data }">
        <span>
          {{ getYesNoColumnText(data?.data?.has_field_delivery_note) }}
        </span>
      </template>
      <DxColumn
        dataField="has_contract"
        cell-template="hasContractViewTemplate"
        :caption="$t('scale.weighing_on_scale.has_contract')"
        :allowHeaderFiltering="false"
        :filter-operations="['=']"
        :allowSorting="false"
        :allowFiltering="true"
        :allowEditing="false"
        :min-width="100"
        :calculateCellValue="getHasContractValue"
      >
        <DxLookup :data-source="yes_no_list" value-expr="id" display-expr="label" />
      </DxColumn>
      <template #hasContractViewTemplate="{ data }">
        <span :title="getContractNumber(data?.data?.weighing_products)">
          {{ getYesNoColumnText(getHasContractValue(data?.data)) }}
        </span>
      </template>
      <DxColumn
        dataField="field_delivery_note_document"        
        :caption="$t('scale.weighing_on_scale.field_delivery_note_document')"
        :allowHeaderFiltering="false"
        :filter-operations="['contains']"
        :allowSorting="false"
        :allowFiltering="true"
        :allowEditing="false"
        :min-width="100"
      />        
      <DxColumn
        dataField="deleted_at"
        cell-template="deletedWeighingViewTemplate"
        :caption="$t('deleted')"
        :allowHeaderFiltering="false"
        :filter-operations="['=']"
        :allowSorting="false"
        :allowFiltering="true"
        :allowEditing="false"
        :min-width="90"
        :calculateCellValue="getYesNoColumnText"
      >
        <DxLookup :data-source="yes_no_list" value-expr="id" display-expr="label" />
      </DxColumn>
      <template #deletedWeighingViewTemplate="{ data }">
        <span>
          {{ getYesNoColumnText(!!data.data.deleted_at) }}
        </span>
      </template>
      <DxColumn
        dataField="date"
        :caption="$t('common.date')"
        :sortOrder="'desc'"
        :allowHeaderFiltering="false"
        :filter-operations="['=']"
        dataType="date"
        :allowEditing="false"
        :format="getDxGridDefaultDateFormat"
        :width="110"
      ></DxColumn>
      <DxColumn
        dataField="scale_house_name"
        :caption="$t('scale.weighing_on_scale.scale_house')"
        :allowHeaderFiltering="false"
        :filter-operations="['contains']"
        :allowSorting="false"
      ></DxColumn>
      <DxColumn
        dataField="document_number"
        :caption="$t('scale.weighing_on_scale.document_number')"
        :allowHeaderFiltering="false"
        :filter-operations="['contains']"
        :allowSorting="false"
      ></DxColumn>
      <DxColumn
        dataField="has_stock_movement"
        cell-template="stockMovementViewTemplate"
        :caption="$t('common.stock')"
        :allowHeaderFiltering="false"
        :filter-operations="['=']"
        :allowSorting="false"
        :allowFiltering="true"
        :allowEditing="false"
        :min-width="90"
        :calculateCellValue="getYesNoColumnText"
      >
        <DxLookup :data-source="yes_no_list" value-expr="id" display-expr="label" />
      </DxColumn>
      <template #stockMovementViewTemplate="{ data }">
        <span>
          {{ getYesNoColumnText(data.data.has_stock_movement) }}
        </span>
      </template>
      <DxColumn
        dataField="valid_stock_document_number"
        :caption="$t('scale.weighing_on_scale.valid_stock_document_number')"
        :allowHeaderFiltering="false"
        :filter-operations="['contains']"
        :allowSorting="false"
        :allowFiltering="true"
        :allowEditing="false"
        cell-template="validStockDocumentCell"
      >
      </DxColumn>
      <template #validStockDocumentCell="{ data }">
        <div class="valid-stock-document-cell">
          <span>{{ data?.data?.valid_stock_document_number || "" }}</span>
          <img
            v-if="data?.data?.document || data?.data?.cancel_document"
            src="~@/assets/images/desktop/icons/controls/pdf.svg"
            class="icon-svg danger-filter valid-stock-document-download"
            @click="handleDownloadStockDocument(data.data)"
            :title="
              (data?.data?.type_name || '') +
              '\n' +
              $t('common.download_document_pdf', {
                document:
                  data?.data?.document?.document_header?.current_model ||
                  data?.data?.cancel_document?.document_header?.current_model ||
                  data?.data?.valid_stock_document_number ||
                  '',
              })
            "
          />
        </div>
      </template>
      <DxColumn
        dataField="weighing_products.product_name"
        :caption="$t('common.product')"
        :allowHeaderFiltering="false"
        :filter-operations="['contains']"
        :allowSorting="false"
      ></DxColumn>
      <DxColumn
        dataField="weighing_products.recipient_warehouse_name"
        :caption="$t('scale.weighing_on_scale.recipient_warehouse')"
        :allowHeaderFiltering="false"
        :filter-operations="['contains']"
        :allowSorting="false"
      ></DxColumn>
      <DxColumn
        dataField="weighing_products.issuer_warehouse_name"
        :caption="$t('scale.weighing_on_scale.issuer_warehouse')"
        :allowHeaderFiltering="false"
        :filter-operations="['contains']"
        :allowSorting="false"
      ></DxColumn>
      <DxColumn
        dataField="parcel_names"
        :caption="$t('works.parcels.parcels')"
        :allowHeaderFiltering="false"
        :filter-operations="['contains']"
        :allowSorting="false"
      ></DxColumn>
      <DxColumn
        dataField="empty_weight_kg"
        :caption="$t('scale.weighing_on_scale.empty_weight')"
        :allowHeaderFiltering="false"
        :dataType="'number'"
        :filter-operations="['contains']"
        :allowSorting="false"
        :width="140"
        alignment="right"
      >
      </DxColumn>
      <DxColumn
        dataField="empty_weight_datetime"
        :caption="$t('scale.weighing_on_scale.empty_weight_date')"
        :allowHeaderFiltering="false"
        dataType="date"
        :allowSorting="false"
        :allowFiltering="false"
        :allowEditing="false"
        :format="getDxGridDefaultDateFormat + ' HH:mm'"
        :calculateCellValue="getEmptyWeightDate"
        :width="150"
      />
      <DxColumn
        dataField="full_weight_kg"
        :caption="$t('scale.weighing_on_scale.full_weight')"
        :dataType="'number'"
        :allowHeaderFiltering="false"
        :filter-operations="['contains']"
        :allowSorting="false"
        :width="140"
        alignment="right"
      ></DxColumn>
      <DxColumn
        dataField="netto_weight_kg"
        :caption="$t('scale.weighing_on_scale.netto_weight')"
        :dataType="'number'"
        :allowHeaderFiltering="false"
        :filter-operations="['contains']"
        :allowSorting="false"
        :width="140"
        alignment="right"
      ></DxColumn>
      <DxColumn
        dataField="full_weight_datetime"
        :caption="$t('scale.weighing_on_scale.full_weight_date')"
        :allowHeaderFiltering="false"
        dataType="date"
        :allowSorting="false"
        :allowFiltering="false"
        :allowEditing="false"
        :format="getDxGridDefaultDateFormat + ' HH:mm'"
        :calculateCellValue="getFullWeightDate"
        :width="150"
      />
      <DxColumn
        dataField="document_data.buyer_seller_name"
        :caption="$t('scale.weighing_on_scale.buyer_seller_name')"
        :allowHeaderFiltering="false"
        :filter-operations="['contains']"
        :allowSorting="false"
      ></DxColumn>
      <DxColumn
        dataField="document_data.partner_name"
        :caption="$t('scale.weighing_on_scale.partner_name')"
        :allowHeaderFiltering="false"
        :filter-operations="['contains']"
        :allowSorting="false"
      ></DxColumn>
      <DxColumn
        dataField="document_data.driver_name"
        :caption="$t('scale.weighing_on_scale.driver_name')"
        :allowHeaderFiltering="false"
        :filter-operations="['contains']"
        :allowSorting="false"
      ></DxColumn>
      <DxColumn
        dataField="document_data.tractor_number_plate"
        :caption="$t('scale.weighing_on_scale.tractor_number_plate')"
        :allowHeaderFiltering="false"
        :filter-operations="['contains']"
        :allowSorting="false"
      ></DxColumn>
      <DxColumn
        dataField="additional_data.moisture"
        :caption="$t('scale.weighing_on_scale.moisture')"
        :allowHeaderFiltering="false"
        :filter-operations="['contains']"
        :allowSorting="false"
      ></DxColumn>
      <DxColumn
        dataField="additional_data.foreign_matter"
        :caption="$t('scale.weighing_on_scale.foreign_matter')"
        :allowHeaderFiltering="false"
        :filter-operations="['contains']"
        :allowSorting="false"
      ></DxColumn>
      <DxColumn
        dataField="additional_data.broken_kernels"
        :caption="$t('scale.weighing_on_scale.broken_kernels')"
        :allowHeaderFiltering="false"
        :filter-operations="['contains']"
        :allowSorting="false"
      ></DxColumn>
      <DxColumn
        dataField="additional_data.protein"
        :caption="$t('scale.weighing_on_scale.protein')"
        :allowHeaderFiltering="false"
        :filter-operations="['contains']"
        :allowSorting="false"
      ></DxColumn>
      <DxColumn
        dataField="additional_data.gluten"
        :caption="$t('scale.weighing_on_scale.gluten')"
        :allowHeaderFiltering="false"
        :filter-operations="['contains']"
        :allowSorting="false"
      ></DxColumn>
      <DxColumn
        dataField="additional_data.hectoliter_mass"
        :caption="$t('scale.weighing_on_scale.hectoliter_mass')"
        :allowHeaderFiltering="false"
        :filter-operations="['contains']"
        :allowSorting="false"
      ></DxColumn>
      <DxColumn
        dataField="additional_data.oil_content"
        :caption="$t('scale.weighing_on_scale.oil_content')"
        :allowHeaderFiltering="false"
        :filter-operations="['contains']"
        :allowSorting="false"
      ></DxColumn>
      <DxColumn
        dataField="additional_data.oleic_acidity"
        :caption="$t('scale.weighing_on_scale.oleic_acidity')"
        :allowHeaderFiltering="false"
        :filter-operations="['contains']"
        :allowSorting="false"
      ></DxColumn>
      <DxColumn
        dataField="additional_data.w_value"
        :caption="$t('scale.weighing_on_scale.w_value')"
        :allowHeaderFiltering="false"
        :filter-operations="['contains']"
        :allowSorting="false"
      ></DxColumn>
      <DxColumn
        dataField="additional_data.crude_oils"
        :caption="$t('scale.weighing_on_scale.crude_oils')"
        :allowHeaderFiltering="false"
        :filter-operations="['contains']"
        :allowSorting="false"
      ></DxColumn>
      <DxColumn
        dataField="additional_data.starch_content"
        :caption="$t('scale.weighing_on_scale.starch_content')"
        :allowHeaderFiltering="false"
        :filter-operations="['contains']"
        :allowSorting="false"
      ></DxColumn>
      <DxColumn
        dataField="additional_data.alcohol"
        :caption="$t('scale.weighing_on_scale.alcohol')"
        :allowHeaderFiltering="false"
        :filter-operations="['contains']"
        :allowSorting="false"
      ></DxColumn>
      <DxColumn
        dataField="additional_data.don_toxin"
        :caption="$t('scale.weighing_on_scale.don_toxin')"
        :allowHeaderFiltering="false"
        :filter-operations="['contains']"
        :allowSorting="false"
      ></DxColumn>
      <DxColumn
        dataField="additional_data.alpha_toxin"
        :caption="$t('scale.weighing_on_scale.alpha_toxin')"
        :allowHeaderFiltering="false"
        :filter-operations="['contains']"
        :allowSorting="false"
      ></DxColumn>
      <DxColumn
        dataField="additional_data.sedimentation"
        :caption="$t('scale.weighing_on_scale.sedimentation')"
        :allowHeaderFiltering="false"
        :filter-operations="['contains']"
        :allowSorting="false"
      ></DxColumn>
      <DxColumn
        dataField="vehicle_configuration"
        :caption="$t('scale.weighing_on_scale.vehicle_configuration')"
        :allowHeaderFiltering="false"
        :filter-operations="['contains']"
        :allowSorting="false"
      ></DxColumn>
      <DxColumn
        dataField="uploading_place"
        :caption="$t('scale.weighing_on_scale.uploading_place')"
        :allowHeaderFiltering="false"
        :filter-operations="['contains']"
        :allowSorting="false"
      ></DxColumn>
      <DxColumn
        dataField="unloading_place"
        :caption="$t('scale.weighing_on_scale.unloading_place')"
        :allowHeaderFiltering="false"
        :filter-operations="['contains']"
        :allowSorting="false"
      ></DxColumn>
      <DxColumn
        dataField="created_by"
        :caption="$t('common.created_by')"
        :allowHeaderFiltering="true"
        :filter-operations="['contains']"
        :allowFiltering="true"
        cell-template="createdByViewTemplate"
        :calculateCellValue="getCreatedByColumnText"
      >
        <DxLookup :data-source="users" value-expr="uuid" display-expr="name" />
        <DxHeaderFilter
          :allowSelectAll="true"
          :search="{
            enabled: true,
            placeholder: $t('common.search'),
          }"
        />
      </DxColumn>
      <template #createdByViewTemplate="{ data }">
        <span>
          {{ getCreatedByColumnText(data.data.created_by) }}
        </span>
      </template>
      <DxColumn
        dataField="updated_at"
        caption="Last Updated"
        dataType="date"
        :allowHeaderFiltering="false"
        :visible="true"
        :allowEditing="false"
        :format="getDxGridDefaultDateFormat + ' HH:mm'"
        :filter-operations="['=']"
      >
      </DxColumn>
      <DxColumn
        :width="70"
        alignment="center"
        :fixed="true"
        fixed-position="right"
        :allow-sorting="false"
        :allow-filtering="false"
        :show-in-column-chooser="false"
        cell-template="actionsCell"
      />
      <template #actionsCell="{ data }">
        <DxActionMenu
          :grid-ref="$refs[gridRefKey]"
          row-key="uuid"
          :row="data.data"
          :items="getWeighingActionItems(data.data)"
          :can-edit="canEdit"
          :can-delete="canDelete"
          :allow-deleting="allowDeletingRow"
          :edit-action="onEditRow"
          :delete-action="onDeleteRow"
          :disabled="exportPdfInProgress"
        />
      </template>
      <DxEditing mode="row" :allowUpdating="false" :allowAdding="false" :allowDeleting="false" :confirm-delete="true" :use-icons="true">
        <DxTexts
          :confirm-delete-message="
            $t('common.confirm_delete', {
              title: $t('scale.weighing_on_scale.title_edit'),
            })
          "
          :confirm-delete-title="$t('common.confirm_delete_title')"
          :deleteRow="$t('common.delete')"
        />
      </DxEditing>
      <DxPaging :total="3500" :page-size="15" />
      <DxPager
        :visible="true"
        :allowed-page-sizes="pageSizes"
        display-mode="compact"
        :show-page-size-selector="true"
        :show-info="true"
        :show-navigation-buttons="true"
        :info-text="$t('common.page_info')"
      />
      <DxHeaderFilter :visible="true" />
      <DxScrolling show-scrollbar="always" :use-native="false" />
      <DxFilterRow :visible="true" :showOperationChooser="true" apply-filter="auto" />
      <DxSelection mode="single" />
      <DxSearchPanel :visible="false" :width="240" />
      <DxGroupPanel :visible="false" :empty-panel-text="$t('common.group_panel_placeholder')" />
      <DxExport :visible="false" :allow-export-selected-data="false" :fileName="'WeighingOnScale'" />
      <DxColumnChooser :visible="false" mode="select" />
      <DxColumnFixing :enabled="true" />
      <DxSummary>
        <DxTotalItem column="empty_weight_kg" summary-type="sum" value-format="#,##0.##" :display-format="$t('common.total') + ': {0}'" />
        <DxTotalItem column="full_weight_kg" summary-type="sum" value-format="#,##0.##" :display-format="$t('common.total') + ': {0}'" />
        <DxTotalItem column="netto_weight_kg" summary-type="sum" value-format="#,##0.##" :display-format="$t('common.total') + ': {0}'" />
      </DxSummary>
    </DxDataGrid>
  </div>
  <div v-else>
    <forbidden></forbidden>
  </div>
</template>

<script>
import { mapActions, mapGetters } from "vuex";
import DxDataGrid, {
  DxColumn,
  DxPaging,
  DxPager,
  DxEditing,
  DxHeaderFilter,
  DxFilterRow,
  DxSelection,
  DxTexts,
  DxButton,
  DxPopup,
  DxItem,
  DxSearchPanel,
  DxExport,
  DxGroupPanel,
  DxColumnChooser,
  DxColumnFixing,
  DxLookup,
  DxSummary,
  DxTotalItem,
  DxScrolling,
} from "devextreme-vue/data-grid";
import DxSelectBox from "devextreme-vue/select-box";
import DxTagBox from "devextreme-vue/tag-box";
import DxDateBox from "devextreme-vue/date-box";
import Forbidden from "~/pages/errors/Forbidden.vue";
import CustomStore from "devextreme/data/custom_store";
import { canView, canDelete, canEdit, hasRole, permissions, permissionTypes } from "~/helpers/Permissions";
import { format, addMonths } from "date-fns";
import { exportDataGrid } from "devextreme/excel_exporter";
import { Workbook } from "exceljs";
import { saveAs } from "file-saver-es";
import notify from "devextreme/ui/notify";
import { errorMessage } from "@/helpers/common";
import { saveDxGridColumnSettings, loadDxGridColumnSettings, dxFilterKeyExist } from "@/helpers/dxGridHelper";
import GridPanelHeader from "@/components/DxGrid/GridPanelHeader.vue";
import GridFilter from "@/components/DxGrid/GridFilter.vue";
import DateRangeFilter from "@/components/DxGrid/DateRangeFilter.vue";
import DxActionMenu from "@/components/DxGrid/DxActionMenu.vue";

export default {
  name: "WeighingOnScale",
  components: {
    DxDataGrid,
    DxColumn,
    DxEditing,
    DxHeaderFilter,
    DxFilterRow,
    DxSelection,
    DxTexts,
    DxButton,
    DxPaging,
    DxPager,
    DxPopup,
    DxItem,
    Forbidden,
    DxSearchPanel,
    DxTagBox,
    DxExport,
    DxGroupPanel,
    DxColumnChooser,
    DxColumnFixing,
    DxSelectBox,
    DxLookup,
    DxSummary,
    DxTotalItem,
    DxScrolling,
    DxDateBox,
    GridPanelHeader,
    GridFilter,
    DateRangeFilter,
    DxActionMenu,
  },
  data() {
    const today = new Date();
    return {
      localWeighingTypes: [],
      grid_page_settings: {},
      filters: {
        created_by_user_uuids: [],
        date_from: addMonths(today, -11),
        date_to: addMonths(today, 1),
      },
      gridAttributes: {
        class: "smagria-grid weighing-on-scale-grid",
      },
      gridRefKey: "weighingOnScaleGrid",
      filterPanelVisible: false,
      weighingOnScaleDataSurce: new CustomStore({
        key: "uuid",
        load: async (loadOptions) => {
          let dataSource = {
            data: [],
            totalCount: 0,
          };

          // not load table data before columnsettings and other page settings  not loaded yet
          if (this.visibilitySettingsLoading || !this.visibilitySettingsLoaded) {
            return dataSource;
          }

          let pageLimit = loadOptions.isLoadingAll ? 99999 : loadOptions.take;
          let pageNumber = (loadOptions.skip + loadOptions.take) / pageLimit;

          let payload = {
            page: pageNumber,
            limit: pageLimit,
            filter: loadOptions.filter,
            sort: loadOptions.sort,
            all_pages_summary_calculation: 1,
          };

          let created_by_found = dxFilterKeyExist("created_by", payload.filter);
          let date_found = dxFilterKeyExist("date", payload.filter);

          if (this.filters.created_by_user_uuids.length > 0 && !created_by_found) {
            if (!payload.filter) {
              payload.filter = [];
            }

            if (payload.filter.length > 0) {
              payload.filter.push("and");
            }

            payload.filter.push(["created_by", "in", this.filters.created_by_user_uuids]);
          }

          if ((this.filters.date_from || this.filters.date_to) && !date_found) {
            if (!payload.filter) {
              payload.filter = [];
            }

            if (payload.filter.length > 0) {
              payload.filter.push("and");
            }

            if (this.filters.date_from) {
              payload.filter.push(["date_from", ">=", this.filters.date_from]);
              if (this.filters.date_to) {
                payload.filter.push("and");
              }
            }

            if (this.filters.date_to) {
              payload.filter.push(["date_to", "<=", this.filters.date_to]);
            }
          }

          try {
            await this.getWeighingOnScale(payload).then((response) => {
              dataSource.data = response.data.data;
              dataSource.totalCount = response.data?.pagination?.total ?? 1;

              if (response.data?.summary && Array.isArray(response.data.summary)) {
                dataSource.summary = response.data.summary;
              } else if (Array.isArray(dataSource.data)) {
                const totals = [0, 0, 0];
                dataSource.data.forEach((row) => {
                  totals[0] += Number(row?.empty_weight_kg) || 0;
                  totals[1] += Number(row?.full_weight_kg) || 0;
                  totals[2] += Number(row?.netto_weight_kg) || 0;
                });
                dataSource.summary = totals;
              }
            });

            return dataSource;
          } catch (error) {
            console.error("Error fetching scale houses:", error);
            throw error;
          }
        },
        remove: this.removeWeighingOnScale,
      }),
      gridRemoteOperations: {
        paging: true,
        filtering: true,
        sorting: true,
        summary: true,
      },
      pageSizes: [15, 50, 100],
      editingRowData: null,
      yes_no_list: [
        {
          id: 1,
          label: this.$t("common.yes"),
        },
        {
          id: 0,
          label: this.$t("common.no"),
        },
      ],
      visibilitySettingsLoaded: false,
      visibilitySettingsLoading: false,
      saveColumnSettingsTimeout: null,
      fieldReportUsaAutomationEnabled: false,
      exportPdfInProgress: false,
    };
  },
  watch: {
    grid_page_settings: {
      handler(newValue) {
        if (newValue && newValue.filters) {
          this.filters.created_by_user_uuids = newValue.filters.created_by_user_uuids || [];
          this.filters.date_from = newValue.filters.date_from || addMonths(new Date(), -11);
          this.filters.date_to = newValue.filters.date_to || addMonths(new Date(), 1);
        }
      },
      immediate: true,
    },
  },
  computed: {
    ...mapGetters({
      weighingOnScaleTypes: "scale/weighingOnScaleTypes",
      users: "user/users",
      getDxGridDefaultDateFormat: "settings/getDxGridDefaultDateFormat",
    }),
    config() {
      return {
        permission: permissions.WEIGHING_ON_SCALE,
        permission_type: permissionTypes.access,
      };
    },
    canView() {
      return canView(this);
    },
    canEdit() {
      return canEdit(this);
    },
    canDelete() {
      return canDelete(this);
    },
    dataGridInstance: function () {
      const grid = this.$refs[this.gridRefKey];
      return grid ? grid.instance : null;
    },
  },
  methods: {
    ...mapActions({
      getWeighingOnScale: "scale/getWeighingOnScale",
      updateWeighingOnScale: "scale/updateWeighingOnScale",
      deleteWeighingOnScale: "scale/deleteWeighingOnScale",
      getWeighingOnScaleTypes: "scale/getWeighingOnScaleTypes",
      downloadPdf: "scale/downloadPdf",
      getExportInfo: "scale/getExportInfo",
      getDxGridSettings: "dxgrid/getDxGridSettings",
      setDxGridSettings: "dxgrid/setDxGridSettings",
      getUsers: "user/getUsers",
      getGroupByCode: "settings/getGroupByCode",
    }),
    openFilterPanel() {
      this.filterPanelVisible = true;
    },
    closeFilterPanel() {
      this.filterPanelVisible = false;
    },
    applyFilters() {
      const instance = this.dataGridInstance;
      if (!instance) {
        this.closeFilterPanel();
        return;
      }

      // Apply the filter and refresh
      instance.refresh();
      this.closeFilterPanel();

      this.updateFilterUsers();
    },
    resetFilters() {
      const instance = this.dataGridInstance;
      if (instance) {
        instance.clearFilter();
        instance.refresh();
      }
      this.filters.created_by_user_uuids = [];
      this.filters.date_from = null;
      this.filters.date_to = null;
      this.closeFilterPanel();

      this.updateFilterUsers();
    },
    exportGrid() {
      const instance = this.dataGridInstance;
      if (!instance) return;

      this.onExporting({
        component: instance,
        format: "xlsx",
        fileName: "WeighingOnScale_" + format(new Date(), "yyyy_MM_dd_hh_mm_ss"),
      });
    },
    handleAddClick(e) {
      this.editingRowData = null;
      this.$router.push({
        name: "weighing-on-scale-edit",
        params: {
          uuid: "add",
        },
      });
    },
    allowDeletingRow(row) {
      // Show the delete button as long as the row is not soft-deleted.
      // The stock-movement guard is handled inside onDeleteRow (after click, before confirm).
      return this.canDelete && row && !row.deleted_at;
    },
    onDeleteRow(row) {
      if (!row) return;

      if (row.has_stock_movement) {
        errorMessage(this, this.$t("scale.weighing_on_scale.error_delete_with_stock_movement"));
        return;
      }

      // Delegate to the grid's built-in delete (shows the confirm dialog).
      const instance = this.dataGridInstance;
      if (!instance) return;
      const rowIndex = instance.getRowIndexByKey(row.uuid);
      if (rowIndex < 0) return;
      instance.deleteRow(rowIndex);
    },
    onEditRow(row) {
      if (!row) return;
      this.onEditingStart({ data: row, cancel: true });
    },
    getWeighingActionItems(row) {
      if (!row) return [];
      const items = [];

      // Transfer action (for edit capability)
      if (this.canEdit && !row.has_stock_movement && !row.deleted_at) {
        items.push({
          key: "transfer_to_new",
          label: this.$t("scale.weighing_on_scale.transfer_to_new_weighing"),
          text: this.$t("scale.weighing_on_scale.transfer_to_new_weighing"),
          icon: require("@/assets/images/icons/arrow_right.svg"),
          action: () => this.handleTransferWeighing(row),
          position: 1,
        });
      }

      // PDF generation for linked transactions
      if (this.hasDocumentGeneratorRole() && row.valid_transaction_uuid) {
        if (row.canceled_at && row.cancel_export_url && !row.cancel_document) {
          items.push({
            key: "generate_cancel_pdf",
            label: this.$t("stock_management.transactions.generate_cancel_pdf", { type: row.type_name || "Transaction" }),
            text: this.$t("stock_management.transactions.generate_cancel_pdf", { type: row.type_name || "Transaction" }),
            icon: require("@/assets/images/icons/transfer.svg"),
            class: "",
            action: () => this.handleExportPdf(row, true),
          });
        }

        if (!row.canceled_at && row.export_url && !row.document) {
          items.push({
            key: "generate_pdf",
            label: this.$t("stock_management.transactions.generate_pdf", { type: row.type_name || "Transaction" }),
            text: this.$t("stock_management.transactions.generate_pdf", { type: row.type_name || "Transaction" }),
            icon: require("@/assets/images/icons/transfer.svg"),
            class: "",
            action: () => this.handleExportPdf(row, false),
          });
        }
      }

      return items;
    },
    handleTransferWeighing(row) {
      if (!row || !row.uuid) return;

      if (row.has_stock_movement) {
        errorMessage(this, this.$t("scale.weighing_on_scale.error_edit_with_stock_movement"));
        return;
      }

      if (row.deleted_at) {
        errorMessage(this, this.$t("scale.weighing_on_scale.error_edit_deleted"));
        return;
      }

      sessionStorage.setItem("weighing_transfer_source_uuid", row.uuid);
      this.$router.push({
        name: "weighing-on-scale-edit",
        params: { uuid: "add" },
      });
    },
    onRowPrepared(e) {
      if (e.rowType === "data") {
        if (e.data.deleted_at) {
          e.rowElement.classList.add("smagria-deleted-row");
        } else if (e.data.has_field_delivery_note == 1) {
          if (e?.data?.finished_weighing == 1) {
            e.rowElement.classList.add("smagria-finished-row");
          } else {
            e.rowElement.classList.add("smagria-unfinished-row");
          }
        }
      }
    },
    onEditingStart(e) {
      e.cancel = true; // Cancel editing for this row
      this.editingRowData = e.data; // Store the row data for editing

      if (e.data.has_stock_movement) {
        errorMessage(this, this.$t("scale.weighing_on_scale.error_edit_with_stock_movement"));
        return;
      }

      if (e.data.deleted_at) {
        errorMessage(this, this.$t("scale.weighing_on_scale.error_edit_deleted"));
        return;
      }

      this.$router.push({
        name: "weighing-on-scale-edit",
        params: {
          uuid: e.data.uuid,
        },
      });
    },
    async removeWeighingOnScale(key) {
      try {
        await this.deleteWeighingOnScale({ uuid: key }).then((response) => {
          this.notifyMsg(response);
        });
      } catch (error) {
        console.error("Error deleting scale :", error);
        throw error;
      }
    },
    onExporting(e) {
      e.fileName = "WeighingOnScale_" + format(new Date(), "yyyy_MM_dd_hh_mm_ss");

      if (e.format === "xlsx") {
        const fields = ["deleted_at", "finished_weighing", "has_stock_movement", "has_field_delivery_note"];
        const workbook = new Workbook();
        const worksheet = workbook.addWorksheet("WeighingOnScale");
        exportDataGrid({
          component: e.component,
          worksheet,
          autoFilterEnabled: true,
          customizeCell: ({ gridCell, excelCell }) => {
            // Skip processing for non-data rows (headers, summaries, etc.)
            if (!gridCell?.data) {
              return;
            }

            // Check if the column is "weighing_type_id"
            if (gridCell?.column?.dataField === "weighing_type_id") {
              excelCell.value = gridCell.value; // Set the display value
            } else if (gridCell?.column?.dataField == "created_by") {
              excelCell.value = this.getCreatedByColumnText(gridCell?.data?.[gridCell?.column?.dataField]);
            } else if (gridCell?.column?.dataField == "has_contract") {
              excelCell.value = this.getYesNoColumnText(this.getHasContractValue(gridCell?.data));
            } else if (fields.includes(gridCell?.column?.dataField)) {
              excelCell.value = this.getYesNoColumnText(!!gridCell?.data?.[gridCell?.column?.dataField]);
            }
          },
        }).then(() => {
          workbook.xlsx.writeBuffer().then((buffer) => {
            saveAs(new Blob([buffer], { type: "application/octet-stream" }), e.fileName + ".xlsx");
          });
        });
      }
    },
    handleDownloadPdf(rowData, isFieldReport = false) {
      this.downloadPdf({
        url: isFieldReport ? rowData.data.field_delivery_note_download_url : rowData.data.download_url,
        filename: isFieldReport ? rowData.data.field_delivery_note_download_filename : rowData.data.download_filename,
        response_type: "blob",
      }).catch((e) => {
        console.error("Error downloading file:", e);
      });
    },
    handleDownloadStockDocument(rowData) {
      if (!rowData) {
        return;
      }

      const isCancel = !rowData.document && !!rowData.cancel_document;
      const url = isCancel ? rowData.cancel_transaction_download_url : rowData.transaction_download_url;
      const filename = isCancel ? rowData.cancel_transaction_download_filename : rowData.transaction_download_filename;
      const type = isCancel ? rowData.cancel_document_uuid : rowData.document_uuid;

      if (!url) {
        return;
      }

      this.downloadPdf({
        type,
        url,
        filename,
        response_type: "blob",
      }).catch((e) => {
        console.error("Error downloading transaction document:", e);

        if (e?.response?.status === 400 || e?.response?.status === 500) {
          if (e.response.data?.errors?.error) {
            errorMessage(this, e.response.data.errors.error);
          } else {
            errorMessage(this, this.$t("error_alert_text"));
          }
        }
      });
    },
    getWeighingTypeName(rowData) {
      const type = this.localWeighingTypes.find((item) => item.weighing_type_id === rowData.weighing_type_id);
      return type ? type.weighing_type : null;
    },
    notifyMsg(response) {
      notify({
        message: response.message ?? "",
        type: response.status == "ok" ? "success" : "error",
        displayTime: 2500,
        width: "auto",
        position: { at: "top center", my: "top center" },
      });
    },
    getYesNoColumnText(value) {
      if (value === null) {
        return "";
      }
      return this.yes_no_list.find((item) => item.id == value)?.label;
    },
    getCreatedByColumnText(value) {
      if (!value) {
        return "";
      }
      const user = this.users.find((user) => user.uuid === value);
      return user ? user.name : this.$t("common.unknown_user");
    },
    getHasContractValue(rowData) {
      if (!rowData) {
        return 0;
      }
      return rowData?.weighing_products?.contract_uuid ? 1 : 0;
    },
    getContractNumber(weighingProducts) {
      if (!weighingProducts?.contract_number) {
        return "";
      }
      return weighingProducts.contract_number;
    },
    getUniqueIdColumnText(rowData) {
      return rowData?.unique_id || rowData?.document_data?.unique_id || "";
    },
    getMeasurementDate(rowData, measurementType) {
      const measurement = rowData?.weighing_measurements?.find(
        (item) => item?.measurement_type?.toUpperCase() === measurementType
      );
      return measurement?.datetime ? new Date(measurement.datetime.replace(" ", "T")) : null;
    },
    getEmptyWeightDate(rowData) {
      return this.getMeasurementDate(rowData, "EMPTY");
    },
    getFullWeightDate(rowData) {
      return this.getMeasurementDate(rowData, "FULL");
    },
    onOptionChanged(e) {
      if (e && e.fullName === "paging.pageSize" && e.value !== e.previousValue) {
        //const instance = this.$refs?.workMachinesGrid?.instance;
        if (this.dataGridInstance) {
          // reset to first page and clear cache so backend fetch starts from page 1 with new limit
          this.dataGridInstance.option("paging.pageIndex", 0);
        }
        this.dataGridInstance?.refresh();
      } else {
        saveDxGridColumnSettings(this, e);
      }
    },
    onContentReady(e) {
      loadDxGridColumnSettings(this);
    },
    updateFilterUsers() {
      this.dataGridInstance?.refresh();
      this.setDxGridSettings({
        grid_identifier: this.gridRefKey,
        grid_page_settings: JSON.stringify({
          filters: {
            created_by_user_uuids: this.filters.created_by_user_uuids,
            date_from: this.filters.date_from,
            date_to: this.filters.date_to,
          },
        }),
      });
    },
    hasDocumentGeneratorRole() {
      return hasRole(this, permissions.STOCK_DOCUMENT_CREATOR);
    },
    handleExportPdf(row, isCancel) {
      if (this.exportPdfInProgress) {
        return;
      }
      this.exportPdfInProgress = true;
      this.getExportInfo({
        url: isCancel ? row.cancel_export_url : row.export_url,
        response_type: "application/json",
      })
        .then((resp) => {
          const transaction = resp.data?.transaction;
          return this.downloadPdf({
            url: isCancel ? transaction?.cancel_download_url : transaction?.download_url,
            filename: isCancel ? transaction?.cancel_download_filename : transaction?.download_filename,
            response_type: "blob",
          });
        })
        .then(() => {
          this.reloadGrid();
        })
        .catch((e) => {
          this.exportPdfInProgress = false;

          if (e?.response?.status === 400 || e?.response?.status === 500) {
            if (e.response.data?.errors?.error) {
              errorMessage(this, e.response.data.errors.error);
            } else {
              errorMessage(this, this.$t("error_alert_text"));
            }
          }
        })
        .finally(() => {
          // Ensure flag reset
        });
    },
    reloadGrid() {
      const instance = this.dataGridInstance;
      if (instance) {
        instance.getDataSource()?.reload?.();
      }
      this.exportPdfInProgress = false;
    },
  },
  mounted() {
    this.getGroupByCode({
      code: "WEIGHING_SCALE",
    }).then((response) => {
      const setting = response?.items?.find((obj) => obj.code == "WEIGHING_SCALE_FIELD_REPORT_USA_AUTOMATION_ENABLED");
      this.fieldReportUsaAutomationEnabled = setting?.value == "1";
    });

    this.getWeighingOnScaleTypes().then((response) => {
      if (response && response.length > 0) {
        this.localWeighingTypes = Object.keys(response).map((key) => {
          return {
            weighing_type_id: response[key].id,
            weighing_type: response[key].name,
          };
        });
      }
    });

    this.getUsers({
      enablePagination: 0,
      limit: 1000,
    });
  },
};
</script>

<style>
.scale-download-pdf-cell {
  background: white !important;
}

.dx-command-edit-with-icons {
  background: white !important;
}

.valid-stock-document-cell {
  display: inline-flex;
  align-items: center;
  gap: 6px;
}

.valid-stock-document-download {
  cursor: pointer;
}
</style>
